import React from 'react';

const PortfolioRisk = () => {
  return (
    <div className="bg-gray-50 rounded-xl p-3 sm:p-4 md:p-5 lg:p-6 shadow-inner w-full h-[22rem] max-w-full overflow-x-auto flex-1">
      <div className="space-y-2 h-full flex flex-col justify-between">
        {/* Risk Analysis Header */}
        <div className="text-center animate-fade-in-down px-1 mb-1">
          <h3 className="text-base font-semibold text-[#113262] mb-1">Portfolio Risk Analysis</h3>
          <p className="text-xs text-[#8A8A8A]">Risk factors identified in your current portfolio</p>
        </div>

        {/* Risk Metrics Grid */}
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {/* Poor Quality Funds */}
          <div className="bg-white p-2 sm:p-2 rounded-md border border-[#FFD5D2] relative animate-slide-in-up hover:shadow transition-all duration-300" style={{ animationDelay: '0.2s' }}>
            <div className="absolute top-2 right-2 bg-[#FFD5D2] text-[#E2483D] px-2 py-1 rounded text-xs font-medium animate-fade-in" style={{ animationDelay: '0.8s' }}>
              43%
            </div>
            <div className="text-center">
              <div className="w-6 h-6 bg-[#E2483D] rounded-full flex items-center justify-center text-white text-xs font-bold mx-auto mb-2 animate-pulse-warning">
                ⚠
              </div>
              <div className="flex items-center justify-center gap-1 mb-2">
                <span className="text-lg font-bold text-[#E2483D] animate-count-up" style={{ animationDelay: '1s' }}>3</span>
                <span className="text-base text-[#8A8A8A]">/</span>
                <span className="text-lg font-bold text-[#113262] animate-count-up" style={{ animationDelay: '1.2s' }}>7</span>
              </div>
              <div className="text-xs font-medium text-[#113262]">Poor Quality</div>
              <div className="h-0.5 bg-gray-200 rounded-full overflow-hidden mt-1">
                <div className="h-full bg-gradient-to-r from-[#E2483D] to-[#C9372C] rounded-full animate-fill-bar" style={{ width: '43%', animationDelay: '1.5s' }}></div>
              </div>
            </div>
          </div>

          {/* Lagging Performance */}
          <div className="bg-white p-2 sm:p-2 rounded-md border border-[#F8E6A0] relative animate-slide-in-up hover:shadow transition-all duration-300" style={{ animationDelay: '0.4s' }}>
            <div className="absolute top-2 right-2 bg-[#F8E6A0] text-[#CF9F02] px-2 py-1 rounded text-xs font-medium animate-fade-in" style={{ animationDelay: '1s' }}>
              29%
            </div>
            <div className="text-center">
              <div className="w-6 h-6 bg-[#CF9F02] rounded-full flex items-center justify-center text-white text-xs font-bold mx-auto mb-2 animate-pulse-warning">
                ↓
              </div>
              <div className="flex items-center justify-center gap-1 mb-2">
                <span className="text-2xl font-bold text-[#CF9F02] animate-count-up" style={{ animationDelay: '1.2s' }}>2</span>
                <span className="text-base text-[#8A8A8A]">/</span>
                <span className="text-lg font-bold text-[#113262] animate-count-up" style={{ animationDelay: '1.4s' }}>7</span>
              </div>
              <div className="text-xs font-medium text-[#113262]">Lagging Funds</div>
              <div className="h-0.5 bg-gray-200 rounded-full overflow-hidden mt-1">
                <div className="h-full bg-gradient-to-r from-[#CF9F02] to-[#B38600] rounded-full animate-fill-bar" style={{ width: '29%', animationDelay: '1.7s' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Risk Summary */}
        <div className="bg-[#FFF7D6] p-2 sm:p-3 rounded-lg border-l-4 border-[#CF9F02] animate-fade-in-up mx-1 sm:mx-0" style={{ animationDelay: '2s' }}>
          <p className="text-xs sm:text-sm text-[#8A8A8A] text-center">
            <span className="font-medium text-[#CF9F02]">5 out of 7 funds</span> may need attention for optimal performance
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in-down {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slide-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes count-up {
          from {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes pulse-warning {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }
        
        @keyframes fill-bar {
          from {
            width: 0;
          }
          to {
            width: var(--target-width);
          }
        }
        
        .animate-fade-in-down {
          animation: fade-in-down 0.8s ease-out both;
        }
        
        .animate-slide-in-up {
          animation: slide-in-up 0.8s ease-out both;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out both;
        }
        
        .animate-count-up {
          animation: count-up 1.5s ease-out both;
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out both;
        }
        
        .animate-pulse-warning {
          animation: pulse-warning 2s infinite;
        }
        
        .animate-fill-bar {
          animation: fill-bar 2s ease-in-out both;
        }
      `}</style>
    </div>
  );
};

export default PortfolioRisk;